import React from 'react';

import styled from '@emotion/styled';

import { Box, Text, FontWeight, COLORS, Label } from '@clutter/clean';

import { Timestamp } from '@shared/components/helpers';

import { useItemActivitiesQuery } from '@portal/schema';

const Separator = styled.hr`
  background: ${COLORS.grayBorder};
  border: none;
  margin: 16px 0;
  height: 1px;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) => {
    switch (status) {
      case 'completed':
        return '#e6f9f0';
      case 'in_progress':
        return '#fff3e0';
      case 'open':
        return '#e3f2fd';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return '#2e7d32';
      case 'in_progress':
        return '#e65100';
      case 'open':
        return '#1565c0';
      default:
        return '#616161';
    }
  }};
`;

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  poor_quality_photos: 'Photo Update',
  missing_labels_on_box: 'Label Verification',
  unwrap_pre_wrap_photo: 'Item Inspection',
  search_in_box: 'Item Search',
  measurements: 'Measurement Update',
  category_change: 'Category Update',
  repack_no_new_item: 'Repacking',
  repack_separate_items: 'Repacking',
};

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress',
  completed: 'Completed',
  open: 'Open',
  canceled: 'Canceled',
};

interface ActivityHistoryProps {
  itemId: string;
}

export const ActivityHistory: React.FC<ActivityHistoryProps> = ({ itemId }) => {
  const { data, loading, error } = useItemActivitiesQuery({
    variables: { itemID: itemId },
  });

  if (loading) return <div>Loading activity...</div>;
  if (error) return <div>Failed to load activity</div>;
  if (!data?.itemActivities.length) return null;

  return (
    <>
      <Separator />
      <Label>Service History</Label>
      <Box margin="8px 0">
        {data.itemActivities.map((activity) => {
          const header = ACTIVITY_TYPE_LABELS[activity.activityType] || activity.description;

          return (
            <Box key={activity.id} padding="8px 0" style={{ borderBottom: `1px solid ${COLORS.grayBorder}` }}>
              <Box.Flex justifyContent="space-between" alignItems="center">
                <Text.Body weight={FontWeight.Medium}>{header}</Text.Body>
                {activity.status && (
                  <StatusBadge status={activity.status}>
                    {STATUS_LABELS[activity.status] || activity.status.replace(/_/g, ' ')}
                  </StatusBadge>
                )}
              </Box.Flex>

              <Text.Callout color={COLORS.hippo} style={{ marginTop: 2 }}>
                <Timestamp value={activity.timestamp} format={Timestamp.Format.DateTime} />
              </Text.Callout>
            </Box>
          );
        })}
      </Box>
    </>
  );
};
